<template>
  <div :class="['flex w-full mb-3', message.role === 'user' ? 'justify-end' : 'justify-start']">
    <div
      :class="[
        'max-w-[85%] px-3 py-2 rounded-2xl',
        message.role === 'user'
          ? 'bg-indigo-600 text-white rounded-br-md'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md',
      ]"
    >
      <div v-if="message.role === 'assistant'" class="chat-prose prose prose-sm dark:prose-invert max-w-none">
        <template v-for="(part, index) in contentParts" :key="index">
          <ChatChart v-if="part.type === 'chart'" :chart-data="part.data" />
          <MarkdownText v-else :markdown="part.content" />
        </template>
        <span v-if="message.isStreaming" class="inline-block w-1.5 h-3 bg-indigo-500 animate-pulse ml-0.5" />
      </div>
      <p v-else class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
      <div
        :class="[
          'text-[10px] mt-0.5',
          message.role === 'user' ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400',
        ]"
      >
        {{ formatTime(message.timestamp) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import throttle from 'lodash/throttle';
import { ChatMessage } from '@/stores/chat.store';
import MarkdownText from '@/components/MarkdownText.vue';
import ChatChart from './ChatChart.vue';
import { parseChartBlocks, formatMessageTime, type ContentPart } from '@/utils/chat.utils';

const props = defineProps<{
  message: ChatMessage;
}>();

const contentParts = ref<ContentPart[]>([]);

const throttledParse = throttle((content: string) => {
  contentParts.value = parseChartBlocks(content);
}, 100);

watch(
  () => props.message.content,
  (newContent) => {
    if (props.message.isStreaming) {
      throttledParse(newContent || '');
    } else {
      throttledParse.cancel();
      contentParts.value = parseChartBlocks(newContent || '');
    }
  },
  { immediate: true },
);

const formatTime = (date: Date): string => {
  return formatMessageTime(date);
};
</script>

<style scoped>
.chat-prose {
  font-size: 0.875rem;
  line-height: 1.5;
}

.chat-prose :deep(p) {
  margin: 0;
}

.chat-prose :deep(p + p) {
  margin-top: 0.3rem;
}

.chat-prose :deep(a) {
  color: #5480cd;
  text-decoration: underline;
  cursor: pointer;
}

.chat-prose :deep(a:hover) {
  color: #3368c4;
}

.chat-prose :deep(code) {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 0.1rem 0.175rem;
  border-radius: 0.175rem;
  font-size: 0.85em;
}

.chat-prose :deep(pre) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 0.4rem;
  border-radius: 0.3rem;
  overflow-x: auto;
  font-size: 0.8rem;
}

.chat-prose :deep(ul) {
  margin: 0.2rem 0;
  padding-left: 1.1rem;
  list-style-type: disc;
}

.chat-prose :deep(ol) {
  margin: 0.2rem 0;
  padding-left: 1.1rem;
  list-style-type: decimal;
}

.chat-prose :deep(li) {
  margin: 0.1rem 0;
  display: list-item;
}

.chat-prose :deep(table) {
  display: block;
  overflow-x: auto;
  white-space: nowrap;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 0.7rem;
}

.chat-prose :deep(th),
.chat-prose :deep(td) {
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 0.25rem 0.5rem;
  text-align: left;
}

.chat-prose :deep(th) {
  background-color: rgba(0, 0, 0, 0.05);
  font-weight: 600;
}

.chat-prose :deep(tr:nth-child(even)) {
  background-color: rgba(0, 0, 0, 0.02);
}

.dark .chat-prose :deep(code) {
  background-color: rgba(255, 255, 255, 0.1);
}

.dark .chat-prose :deep(pre) {
  background-color: rgba(0, 0, 0, 0.3);
}

.dark .chat-prose :deep(th),
.dark .chat-prose :deep(td) {
  border-color: rgba(255, 255, 255, 0.15);
}

.dark .chat-prose :deep(th) {
  background-color: rgba(255, 255, 255, 0.05);
}

.dark .chat-prose :deep(tr:nth-child(even)) {
  background-color: rgba(255, 255, 255, 0.02);
}

.dark .chat-prose :deep(a) {
  color: #8aa8dd;
}

.dark .chat-prose :deep(a:hover) {
  color: #a8c0e8;
}
</style>
